import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSupabaseAuth } from "contexts/SupabaseAuthContext";

// material-ui
import { useTheme, alpha } from "@mui/material/styles";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CardActions from "@mui/material/CardActions";
import Chip from "@mui/material/Chip";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import IconButton from "@mui/material/IconButton";

// third-party
import PerfectScrollbar from "react-perfect-scrollbar";

import { useSelector, useDispatch } from "react-redux";
import {
  selectNoticesLoaded,
  fetchSupabaseNotificationLogs,
  addLog,
  updateLog,
  removeLog,
  markAllNotificationsAsRead,
} from "store/NotificationSlice";
import { supabase } from "services/supabase";

// project imports
import MainCard from "uicomponent/cards/MainCard";
import Transitions from "uicomponent/extended/Transitions";
import NotificationList from "./NotificationList";

// assets
import NotificationsIcon from "@mui/icons-material/Notifications";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
// notification status options
const status = [
  {
    value: "all",
    label: "All Notifications",
  },
  {
    value: "new",
    label: "New",
  },
  {
    value: "unread",
    label: "Unread",
  },
  {
    value: "other",
    label: "Other",
  },
];

// ==============================|| NOTIFICATION ||============================== //

// Keyframes for bell jiggle animation
const bellJiggle = {
  "@keyframes bellJiggle": {
    "0%": {
      transform: "rotate(0deg)",
    },
    "10%": {
      transform: "rotate(-30deg)",
    },
    "30%": {
      transform: "rotate(30deg)",
    },
    "50%": {
      transform: "rotate(-30deg)",
    },
    "70%": {
      transform: "rotate(30deg)",
    },
    "90%": {
      transform: "rotate(-30deg)",
    },

    "100%": {
      transform: "rotate(0deg)",
    },
  },
};

const NotificationSection = () => {
  const theme = useTheme();
  const matchesXs = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const { userId } = useSupabaseAuth();

  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  /**
   * anchorRef is used on different componets and specifying one type leads to other components throwing an error
   * */
  const anchorRef = useRef(null);

  const noticesLoaded = useSelector(selectNoticesLoaded);
  const notices = useSelector((state) => state.notificationReducer.notices);
  const loading = useSelector((state) => state.notificationReducer.loading);
  const dispatch = useDispatch();
  const [loaded, setLoaded] = useState(false);
  const subscriptionRef = useRef(null);

  // Set up Supabase real-time subscription for logs table
  useEffect(() => {
    let mounted = true;

    const setupRealtimeSubscription = async () => {
      try {
        if (!userId) {
          console.error("User not authenticated");
          return;
        }

        // Initial fetch of logs
        if (!noticesLoaded) {
          await dispatch(fetchSupabaseNotificationLogs({ userId }));
        }

        // Create real-time subscription
        const subscription = supabase
          .channel("logs_changes")
          .on(
            "postgres_changes",
            {
              event: "*", // Listen to all changes (INSERT, UPDATE, DELETE)
              schema: "public",
              table: "logs",
              filter: `uid=eq.${userId}`, // Only listen to logs for current user
            },
            (payload) => {
              console.log("Real-time log update:", payload);

              // Handle different event types
              switch (payload.eventType) {
                case "INSERT":
                  console.log("Adding new log:", payload.new);
                  dispatch(addLog(payload.new));
                  break;
                case "UPDATE":
                  console.log("Updating log:", payload.new);
                  dispatch(updateLog(payload.new));
                  break;
                case "DELETE":
                  console.log("Removing log:", payload.old);
                  dispatch(removeLog(payload.old));
                  break;
                default:
                  // Fallback: refetch all logs
                  console.log("Refetching all logs");
                  dispatch(fetchSupabaseNotificationLogs({ userId }));
              }
            }
          )
          .subscribe();

        subscriptionRef.current = subscription;

        if (mounted) {
          setLoaded(true);
        }
      } catch (error) {
        console.error("Error setting up real-time subscription:", error);
        if (mounted) {
          setLoaded(true);
        }
      }
    };

    setupRealtimeSubscription();

    // Cleanup subscription when component unmounts
    return () => {
      mounted = false;
      if (subscriptionRef.current) {
        supabase.removeChannel(subscriptionRef.current);
        subscriptionRef.current = null;
      }
    };
  }, [dispatch, noticesLoaded]);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }
    prevOpen.current = open;
  }, [open]);

  const handleChange = (event) => {
    if (event?.target.value) setValue(event?.target.value);
  };

  const handleViewAll = () => {
    setOpen(false); // Close the notification dropdown
    navigate("/history"); // Navigate to history page
  };

  const handleMarkAllAsRead = () => {
    if (!userId) return;

    dispatch(markAllNotificationsAsRead({ userId }))
      .unwrap()
      .catch((err) => {
        console.error("handleMarkAllAsRead failed:", err);
      });
  };

  return (
    <>
      <Box sx={{ position: "relative" }}>
        <IconButton
          sx={{
            width: "48px",
            height: "48px",
            ...(theme.palette.mode === "dark"
              ? {
                borderRadius: "16px",
                color: theme.palette.grey[200],
                backgroundColor: alpha(theme.palette.common.white, 0.08),
                transition: "background-color .2s ease, color .2s ease",
                "&:hover": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.35),
                  color: theme.palette.primary.contrastText,
                },
              }
              : {}),
          }}
          ref={anchorRef}
          aria-controls={open ? "menu-list-grow" : undefined}
          aria-haspopup="true"
          onClick={handleToggle}
        >
          <Box
            sx={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {/* Static bell icon */}
            {notices.length === 0 ? (
              <NotificationsIcon stroke={1.5} size="1.3rem" />
            ) : (
              <NotificationsActiveIcon
                stroke={1.5}
                size="1.3rem"
                sx={{
                  position: "absolute",
                  // top: 0,
                  // left: 0,
                  transformOrigin: "top center",
                  ...bellJiggle,
                  animation: "bellJiggle 1s ease-in-out 1",
                }}
              />
            )}
          </Box>
        </IconButton>
      </Box>
      <Popper
        placement={matchesXs ? "bottom" : "bottom-end"}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        sx={{
          "& .MuiPaper-root": {
            minWidth: 550,
            maxWidth: 600,
            [theme.breakpoints.down("md")]: {
              minWidth: 500,
              maxWidth: 550,
            },
          },
        }}
        popperOptions={{
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [matchesXs ? 5 : 0, 20],
              },
            },
          ],
        }}
      >
        {({ TransitionProps }) => (
          <Transitions
            position={matchesXs ? "top" : "top-right"}
            in={open}
            {...TransitionProps}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard
                  border={false}
                  elevation={16}
                  content={false}
                  boxShadow
                  shadow={theme.shadows[16]}
                >
                  <Grid container direction="column" spacing={2}>
                    <Grid size={12}>
                      <Grid
                        container
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ pt: 2, px: 2 }}
                      >
                        <Grid>
                          <Stack direction="row" spacing={2}>
                            <Typography variant="subtitle1">
                              Notifications
                            </Typography>
                          </Stack>
                        </Grid>
                        {notices && notices.length > 0 && (
                          <Grid>
                            <Button
                              size="small"
                              disableElevation
                              onClick={handleMarkAllAsRead}
                            >
                              Mark as all read
                            </Button>
                          </Grid>
                        )}
                      </Grid>
                    </Grid>
                    <Grid size={12}>
                      {/* <PerfectScrollbar
                        style={{
                          // height: "100%",
                          // maxHeight: "calc(100vh - 205px)",
                          // overflowX: "hidden",
                        }}
                      > */}
                      <Grid container direction="column" spacing={2}>
                        <Grid size={12}>
                          <Box sx={{ px: 2, pt: 0.25 }}>
                            <TextField
                              id="outlined-select-currency-native"
                              select
                              fullWidth
                              value={value}
                              onChange={handleChange}
                              SelectProps={{
                                native: true,
                              }}
                            >
                              {status.map((option) => (
                                <option
                                  key={option.value}
                                  value={option.value}
                                >
                                  {option.label}
                                </option>
                              ))}
                            </TextField>
                          </Box>
                        </Grid>
                        <Grid p={0} size={12}>
                          <Divider sx={{ my: 0 }} />
                        </Grid>
                      </Grid>
                      <NotificationList />
                      {/* </PerfectScrollbar> */}
                    </Grid>
                  </Grid>
                  <Divider />
                  <CardActions sx={{ p: 0, justifyContent: "center" }}>
                    <Button
                      sx={{ p: 1.25 }}
                      size="small"
                      fullWidth
                      disableElevation
                      onClick={handleViewAll}
                    >
                      View All
                    </Button>
                  </CardActions>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </>
  );
};

export default NotificationSection;
